import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cachedTasksService } from '../../services/cachedTasksService';

// Helper to format error message from payload
const formatErrorMessage = (payload) => {
  if (!payload) return 'An unknown error occurred';
  if (typeof payload === 'string') return payload;
  if (payload.error && typeof payload.error === 'string') return payload.error;
  if (payload.detail && typeof payload.detail === 'string') return payload.detail;
  if (payload.message && typeof payload.message === 'string') return payload.message;

  // Handle nested objects or arrays (common in DRF validation)
  try {
    return JSON.stringify(payload);
  } catch (e) {
    return 'An error occurred but could not be formatted';
  }
};

// Async thunks for API calls
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (params, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.getTasks(params);
      return response.data;
    } catch (error) {
      console.error('❌ Redux: fetchTasks error:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTask = createAsyncThunk(
  'tasks/fetchTask',
  async (id, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.getTask(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async (taskData, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.createTask(taskData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.updateTask(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (id, { rejectWithValue }) => {
    try {
      await cachedTasksService.deleteTask(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const assignTask = createAsyncThunk(
  'tasks/assignTask',
  async (assignmentData, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.assignTask(assignmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkUpdateTasks = createAsyncThunk(
  'tasks/bulkUpdateTasks',
  async (bulkData, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.bulkUpdateTasks(bulkData);
      return { bulkData, response: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDashboardData = createAsyncThunk(
  'tasks/fetchDashboardData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.getDashboardData();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkDeleteTasks = createAsyncThunk(
  'tasks/bulkDeleteTasks',
  async (taskIds, { rejectWithValue }) => {
    try {
      await cachedTasksService.bulkDeleteTasks(taskIds);
      return taskIds;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTaskComments = createAsyncThunk(
  'tasks/fetchTaskComments',
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.getTaskComments(taskId);
      return { taskId, comments: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addTaskComment = createAsyncThunk(
  'tasks/addTaskComment',
  async ({ taskId, comment }, { rejectWithValue }) => {
    try {
      const response = await cachedTasksService.addTaskComment(taskId, comment);
      return { taskId, comment: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Optimized initial state - reduced complexity
const initialState = {
  tasks: [],
  selectedTask: null,
  taskComments: {},
  dashboardData: {
    total_tasks: 0,
    not_started_tasks: 0,
    in_progress_tasks: 0,
    on_hold_tasks: 0,
    completed_tasks: 0,
    overdue_tasks: 0,
    pending_tasks: 0,
    blocked_tasks: 0,
    cancelled_tasks: 0
  },
  filters: {
    search: '',
    status: [],
    assignee: '',
    project: ''
  },
  sorting: {
    field: 'planned_end_date',
    direction: 'asc'
  },
  pagination: {
    page: 1,
    limit: 10, // Reduced from 25 to 10 for faster loading
    total: 0,
    totalPages: 0
  },
  selectedTasks: [],
  loading: false,
  dashboardLoading: false,
  error: null,
  bulkOperationLoading: false,
  commentsLoading: {}
};

// Slice
const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1; // Reset to first page when filters change
    },
    setSorting: (state, action) => {
      state.sorting = action.payload;
      state.pagination.page = 1; // Reset to first page when sorting changes
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setSelectedTasks: (state, action) => {
      state.selectedTasks = action.payload;
    },
    toggleTaskSelection: (state, action) => {
      const taskId = action.payload;
      if (state.selectedTasks.includes(taskId)) {
        state.selectedTasks = state.selectedTasks.filter(id => id !== taskId);
      } else {
        state.selectedTasks.push(taskId);
      }
    },
    selectAllTasks: (state) => {
      state.selectedTasks = state.tasks.map(task => task.id);
    },
    deselectAllTasks: (state) => {
      state.selectedTasks = [];
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateTaskRealtime: (state, action) => {
      const updatedTask = action.payload;
      const index = state.tasks.findIndex(task => task.id === updatedTask.id);
      if (index !== -1) {
        state.tasks[index] = updatedTask;
      }
      if (state.selectedTask && state.selectedTask.id === updatedTask.id) {
        state.selectedTask = updatedTask;
      }
    },
    addTaskRealtime: (state, action) => {
      const newTask = action.payload;
      // Only add if not already present to prevent duplicates
      if (!state.tasks.find(task => task.id === newTask.id)) {
        state.tasks.unshift(newTask);
        state.pagination.total += 1;
      }
    },
    deleteTaskRealtime: (state, action) => {
      const taskId = action.payload;
      state.tasks = state.tasks.filter(task => task.id !== taskId);
      state.selectedTasks = state.selectedTasks.filter(id => id !== taskId);
      if (state.selectedTask && state.selectedTask.id === taskId) {
        state.selectedTask = null;
      }
      state.pagination.total -= 1;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks
      .addCase(fetchTasks.pending, (state, action) => {
        // Only set global loading for initial non-background requests
        if (!action.meta.arg?.background) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        const tasks = action.payload.results || action.payload;
        // Only update if tasks actually changed to prevent unnecessary re-renders
        state.tasks = tasks;
        if (action.payload.count !== undefined) {
          state.pagination.total = action.payload.count;
          state.pagination.totalPages = Math.ceil(action.payload.count / state.pagination.limit);
        }
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = formatErrorMessage(action.payload);
      })

      // Fetch single task
      .addCase(fetchTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTask.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTask = action.payload;
        // Update task in list if it exists
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(fetchTask.rejected, (state, action) => {
        state.loading = false;
        state.error = formatErrorMessage(action.payload);
      })

      // Create task
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = formatErrorMessage(action.payload);
      })

      // Update task
      .addCase(updateTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex(task => task.id === action.payload.id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
        if (state.selectedTask && state.selectedTask.id === action.payload.id) {
          state.selectedTask = action.payload;
        }
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.loading = false;
        state.error = formatErrorMessage(action.payload);
      })

      // Delete task
      .addCase(deleteTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter(task => task.id !== action.payload);
        state.selectedTasks = state.selectedTasks.filter(id => id !== action.payload);
        if (state.selectedTask && state.selectedTask.id === action.payload) {
          state.selectedTask = null;
        }
        state.pagination.total -= 1;
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.loading = false;
        state.error = formatErrorMessage(action.payload);
      })

      // Assign task
      .addCase(assignTask.pending, (state) => {
        state.error = null;
      })
      .addCase(assignTask.fulfilled, (state, action) => {
        // Task assignment is handled by real-time updates or refetch
      })
      .addCase(assignTask.rejected, (state, action) => {
        state.error = formatErrorMessage(action.payload);
      })

      // Bulk operations
      .addCase(bulkUpdateTasks.pending, (state) => {
        state.bulkOperationLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateTasks.fulfilled, (state, action) => {
        state.bulkOperationLoading = false;
        state.selectedTasks = [];

        const { bulkData } = action.payload;
        const { task_ids, update_data } = bulkData;

        if (state.tasks.length > 0) {
          state.tasks = state.tasks.map(task => {
            if (task_ids.includes(task.id)) {
              return { ...task, ...update_data };
            }
            return task;
          });
        }
      })
      .addCase(bulkUpdateTasks.rejected, (state, action) => {
        state.bulkOperationLoading = false;
        state.error = formatErrorMessage(action.payload);
      })

      .addCase(bulkDeleteTasks.pending, (state) => {
        state.bulkOperationLoading = true;
        state.error = null;
      })
      .addCase(bulkDeleteTasks.fulfilled, (state, action) => {
        state.bulkOperationLoading = false;
        const deletedIds = action.payload;
        state.tasks = state.tasks.filter(task => !deletedIds.includes(task.id));
        state.selectedTasks = [];
        state.pagination.total -= deletedIds.length;
      })
      .addCase(bulkDeleteTasks.rejected, (state, action) => {
        state.bulkOperationLoading = false;
        state.error = formatErrorMessage(action.payload);
      })

      // Comments
      .addCase(fetchTaskComments.pending, (state, action) => {
        const taskId = action.meta.arg;
        state.commentsLoading[taskId] = true;
      })
      .addCase(fetchTaskComments.fulfilled, (state, action) => {
        const { taskId, comments } = action.payload;
        state.taskComments[taskId] = comments;
        state.commentsLoading[taskId] = false;
      })
      .addCase(fetchTaskComments.rejected, (state, action) => {
        const taskId = action.meta.arg;
        state.commentsLoading[taskId] = false;
      })

      .addCase(addTaskComment.fulfilled, (state, action) => {
        const { taskId, comment } = action.payload;
        if (!state.taskComments[taskId]) {
          state.taskComments[taskId] = [];
        }
        state.taskComments[taskId].unshift(comment);
      })

      // Dashboard data
      .addCase(fetchDashboardData.pending, (state) => {
        console.log('🔄 Redux: fetchDashboardData.pending - Starting to load dashboard data');
        state.dashboardLoading = true;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        console.log('✅ Redux: fetchDashboardData.fulfilled - Received data:', action.payload);
        state.dashboardLoading = false;
        state.dashboardData = action.payload;
        console.log('✅ Redux: Dashboard data updated in state:', state.dashboardData);
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        console.error('❌ Redux: fetchDashboardData.rejected - Error:', action.payload);
        state.dashboardLoading = false;
        state.error = formatErrorMessage(action.payload);
      });
  }
});

// Export actions
export const {
  setFilters,
  setSorting,
  setPagination,
  setSelectedTasks,
  toggleTaskSelection,
  selectAllTasks,
  deselectAllTasks,
  clearFilters,
  clearError,
  updateTaskRealtime,
  addTaskRealtime,
  deleteTaskRealtime
} = tasksSlice.actions;

// Selectors
export const selectTasks = (state) => state.tasks.tasks;
export const selectSelectedTask = (state) => state.tasks.selectedTask;
export const selectTasksLoading = (state) => state.tasks.loading;
export const selectTasksError = (state) => state.tasks.error;
export const selectFilters = (state) => state.tasks.filters;
export const selectSorting = (state) => state.tasks.sorting;
export const selectPagination = (state) => state.tasks.pagination;
export const selectSelectedTasks = (state) => state.tasks.selectedTasks;
export const selectBulkOperationLoading = (state) => state.tasks.bulkOperationLoading;
export const selectTaskComments = (taskId) => (state) => state.tasks.taskComments[taskId] || [];
export const selectCommentsLoading = (taskId) => (state) => state.tasks.commentsLoading[taskId] || false;
export const selectDashboardData = (state) => state.tasks.dashboardData;
export const selectDashboardLoading = (state) => state.tasks.dashboardLoading;

// Computed selectors
export const selectFilteredTasksCount = (state) => state.tasks.pagination.total;
export const selectHasSelectedTasks = (state) => state.tasks.selectedTasks.length > 0;
export const selectAllTasksSelected = (state) => {
  const { tasks, selectedTasks } = state.tasks;
  return tasks.length > 0 && selectedTasks.length === tasks.length;
};

export default tasksSlice.reducer;